import {
  arrow,
  autoUpdate,
  FloatingArrow,
  FloatingPortal,
  flip as flipMiddleware,
  offset as offsetMiddleware,
  type Placement,
  safePolygon,
  shift as shiftMiddleware,
  useClick,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
  useRole,
  useTransitionStyles,
} from "@floating-ui/react"
import classNames from "classnames"
import type React from "react"
import { useEffect, useRef } from "react"
import { zIndexes } from "../../tokens"

const ANIMATION_TRANSITION_DISTANCE_PX = 4

export type PopoverConfig = {
  placement?: Placement
  flip?: boolean
  arrow?: boolean
  theme?: string
  tooltip?: boolean
  lightMode?: boolean
  zIndex?: number
  appendToBody?: boolean
  fixed?: boolean
  withBorder?: boolean
  fitContent?: boolean
  trigger?: "click" | "mouseenter"
  interactive?: boolean
  delay?: number | { open?: number; close?: number }
  offset?: [number, number]
  bodySpacing?: boolean
  onShow?: () => void
  onHidden?: () => void
}

export type UseDesktopPopoverCoreOptions = Omit<PopoverConfig, "trigger"> & {
  isOpen: boolean
  onOpenChange?: (open: boolean) => void
  referenceElement: Element | null
  trigger?: "click" | "mouseenter" | "manual"
  ariaLabel?: string
  getFloatingExtraProps?: () => React.HTMLAttributes<HTMLElement>
}

export type DesktopPopoverCoreRefs = {
  setReference: (node: Element | null) => void
  setFloating: (node: HTMLElement | null) => void
}

type UseDesktopPopoverCoreReturnType = {
  refs: DesktopPopoverCoreRefs
  getReferenceProps: ReturnType<
    typeof import("@floating-ui/react").useInteractions
  >["getReferenceProps"]
  renderFloating: (content: React.ReactNode) => React.ReactNode
  isMounted: boolean
}

export function useDesktopPopoverCore({
  isOpen,
  onOpenChange,
  referenceElement,
  placement = "top",
  flip = true,
  tooltip = false,
  arrow: showArrow = true,
  theme = "",
  zIndex = zIndexes.dropdown,
  appendToBody = true,
  fixed = false,
  fitContent = true,
  withBorder = false,
  trigger = "click",
  interactive = false,
  delay = 0,
  offset,
  bodySpacing = false,
  ariaLabel,
  onShow,
  onHidden,
  getFloatingExtraProps,
}: UseDesktopPopoverCoreOptions): UseDesktopPopoverCoreReturnType {
  const arrowRef = useRef<SVGSVGElement | null>(null)

  const [crossAxis, mainAxis] = offset ?? [0, showArrow ? 12 : 8]

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange,
    placement,
    strategy: fixed ? "fixed" : "absolute",
    transform: false,
    middleware: [
      shiftMiddleware({ padding: 8 }),
      offsetMiddleware({ mainAxis, crossAxis }),
      ...(flip ? [flipMiddleware()] : []),
      ...(showArrow ? [arrow({ element: arrowRef })] : []),
    ],
    whileElementsMounted: autoUpdate,
    elements: { reference: referenceElement },
  })

  const isHover = trigger === "mouseenter"
  const isClick = trigger === "click"

  const hover = useHover(context, {
    enabled: isHover,
    move: false,
    delay,
    handleClose: isHover && interactive ? safePolygon() : undefined,
  })

  const click = useClick(context, {
    enabled: isClick,
  })

  const dismiss = useDismiss(context)
  const role = useRole(context, { role: "dialog" })

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    click,
    dismiss,
    role,
  ])

  const { isMounted, styles: transitionStyles } = useTransitionStyles(context, {
    duration: { open: 200, close: 150 },
    initial: ({ side }) => ({
      opacity: 0,
      transform:
        side === "top"
          ? `translateY(${ANIMATION_TRANSITION_DISTANCE_PX}px)`
          : side === "bottom"
            ? `translateY(-${ANIMATION_TRANSITION_DISTANCE_PX}px)`
            : side === "left"
              ? `translateX(${ANIMATION_TRANSITION_DISTANCE_PX}px)`
              : `translateX(-${ANIMATION_TRANSITION_DISTANCE_PX}px)`,
    }),
  })

  const prevMountedRef = useRef(false)

  useEffect(() => {
    if (isMounted && !prevMountedRef.current) onShow?.()
    if (!isMounted && prevMountedRef.current) onHidden?.()
    prevMountedRef.current = isMounted
  }, [isMounted, onShow, onHidden])

  const renderFloating = (content: React.ReactNode) => {
    if (!isMounted) return null

    const body = bodySpacing ? (
      <div className="cobalt-popover--bodySpacing">{content}</div>
    ) : (
      content
    )

    const extraFloatingProps = getFloatingExtraProps?.() ?? {}
    const { onMouseEnter, onMouseLeave, ...restExtraFloatingProps } =
      extraFloatingProps

    const node = (
      <div
        ref={refs.setFloating}
        style={{
          ...floatingStyles,
          ...transitionStyles,
          zIndex,
        }}
        className={classNames("cobalt-popover", {
          "cobalt-popover--withArrow": showArrow,
          "cobalt-popover--fitContent": fitContent,
          "cobalt-popover--withBorder": withBorder,
        })}
        data-placement={context.placement}
        data-theme={classNames(theme, {
          tooltip: tooltip,
        })}
        {...getFloatingProps({
          "aria-label": ariaLabel,
          ...restExtraFloatingProps,
          onMouseEnter,
          onMouseLeave,
        })}
      >
        {showArrow && (
          <FloatingArrow
            ref={arrowRef}
            context={context}
            className="cobalt-popover__arrow"
          />
        )}
        {body}
      </div>
    )

    return appendToBody ? <FloatingPortal>{node}</FloatingPortal> : node
  }

  return {
    refs: {
      setReference: refs.setReference,
      setFloating: refs.setFloating,
    },
    getReferenceProps,
    renderFloating,
    isMounted,
  }
}
